import { useMemo, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { useLocation } from "wouter";
import {
  AlertTriangle,
  ArrowUpRight,
  Bug,
  CheckCircle2,
  ClipboardCheck,
  CreditCard,
  Handshake,
  ListTodo,
  Mail,
  MessageSquare,
  UserPlus,
  Users,
} from "lucide-react";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Progress } from "../../components/ui/progress";
import { getApiBaseUrl } from "../../lib/queryClient";
import { cn } from "../../lib/utils";

type Deal = {
  id: number;
  name: string;
  value?: string | number | null;
  stage?: string | null;
  priority?: string | null;
  clientId?: number | null;
  clientName?: string | null;
  clientEmail?: string | null;
  clientPhone?: string | null;
  updatedAt?: string | null;
  createdAt?: string | null;
  stakeholdersCount?: number | null;
};

type Client = {
  id: number;
  name: string;
  email?: string | null;
  phone?: string | null;
  status?: string | null;
  source?: string | null;
  createdAt?: string | null;
};

type Task = {
  id: number;
  title: string;
  company?: string | null;
  project?: string | null;
  status?: string | null;
  priority?: string | null;
  due_date?: string | null;
};

type TicketStats = {
  total: number;
  byStatus?: Record<string, number>;
  totalUnbilledAmount?: number;
};

type BillingDashboard = {
  totalRevenue?: number;
  monthlyRevenue?: number;
  outstandingBalance?: number;
  unpaidAmount?: number;
};

const ownerActions = [
  {
    label: "Send onboarding",
    detail: "Client intake, access, billing, software, hardware",
    icon: UserPlus,
    href: "/admin/clients",
  },
  {
    label: "Review active deals",
    detail: "Scope, status, money, next steps",
    icon: Handshake,
    href: "/admin/deals",
  },
  {
    label: "Triage bug reports",
    detail: "Client issues into developer tracker",
    icon: Bug,
    href: "/admin/tickets",
  },
  {
    label: "Launch readiness",
    detail: "Credentials, handoff, deployment plan",
    icon: ClipboardCheck,
    href: "/admin/production-readiness",
  },
];

const bsaProtocols = [
  "Account form before work starts",
  "Hardware model numbers when hardware is involved",
  "Software stack and credentials on file",
  "BillingOps setup before recurring work",
  "Launch handoff with credentials and support channel",
  "Bug reports routed into developer tracker",
];

function getToken() {
  return localStorage.getItem("authToken") || localStorage.getItem("token") || "";
}

async function fetchAdminJson<T>(path: string): Promise<T> {
  const response = await fetch(`${getApiBaseUrl()}${path}`, {
    headers: { Authorization: `Bearer ${getToken()}` },
  });
  if (!response.ok) throw new Error(`Failed to load ${path}`);
  return response.json();
}

function currency(value: string | number | null | undefined) {
  const amount = Number(value || 0);
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(Number.isFinite(amount) ? amount : 0);
}

function dateLabel(value?: string | null) {
  if (!value) return "No date";
  return new Date(value).toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function statusClass(stage?: string | null) {
  switch (stage) {
    case "active":
    case "won":
    case "resolved":
      return "bg-emerald-50 text-emerald-700 border-emerald-200";
    case "proposal":
    case "negotiation":
    case "in_progress":
      return "bg-blue-50 text-blue-700 border-blue-200";
    case "lost":
    case "urgent":
      return "bg-red-50 text-red-700 border-red-200";
    default:
      return "bg-slate-100 text-slate-700 border-slate-200";
  }
}

function Panel({
  title,
  eyebrow,
  action,
  children,
}: {
  title: string;
  eyebrow?: string;
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="rounded-lg border border-slate-200 bg-white">
      <div className="flex items-start justify-between gap-4 border-b border-slate-100 px-5 py-4">
        <div>
          {eyebrow ? <div className="text-xs font-bold uppercase tracking-[0.08em] text-slate-400">{eyebrow}</div> : null}
          <h2 className="mt-1 text-base font-black text-slate-950">{title}</h2>
        </div>
        {action}
      </div>
      <div className="p-5">{children}</div>
    </section>
  );
}

function Metric({
  label,
  value,
  icon: Icon,
  tone = "slate",
}: {
  label: string;
  value: string;
  icon: typeof Users;
  tone?: "slate" | "blue" | "orange" | "emerald";
}) {
  const tones = {
    slate: "bg-slate-100 text-slate-700",
    blue: "bg-blue-50 text-blue-700",
    orange: "bg-orange-50 text-orange-700",
    emerald: "bg-emerald-50 text-emerald-700",
  };

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4">
      <div className="flex items-center justify-between">
        <span className="text-sm font-semibold text-slate-500">{label}</span>
        <span className={cn("flex h-9 w-9 items-center justify-center rounded-md", tones[tone])}>
          <Icon className="h-4 w-4" />
        </span>
      </div>
      <div className="mt-4 text-2xl font-black tracking-tight text-slate-950">{value}</div>
    </div>
  );
}

export default function DesktopMissionControlPage() {
  const [, navigate] = useLocation();

  const dealsQuery = useQuery<{ success: boolean; deals?: Deal[]; data?: Deal[] }>({
    queryKey: ["/api/admin/deals"],
  });
  const clientsQuery = useQuery<{ success: boolean; clients?: Client[] }>({
    queryKey: ["desktop-admin-clients"],
    queryFn: () => fetchAdminJson("/admin/clients?hideHidden=true&limit=200"),
  });
  const tasksQuery = useQuery<Task[]>({
    queryKey: ["/api/admin/knowledge-base/actions"],
  });
  const ticketStatsQuery = useQuery<{ success: boolean; stats?: TicketStats }>({
    queryKey: ["desktop-admin-ticket-stats"],
    queryFn: () => fetchAdminJson("/admin/tickets/stats"),
  });
  const billingQuery = useQuery<{ success: boolean; dashboard?: BillingDashboard; data?: BillingDashboard }>({
    queryKey: ["desktop-admin-billing"],
    queryFn: () => fetchAdminJson("/admin/billing/dashboard"),
  });

  const deals = dealsQuery.data?.deals || dealsQuery.data?.data || [];
  const clients = clientsQuery.data?.clients || [];
  const tasks = Array.isArray(tasksQuery.data) ? tasksQuery.data : [];
  const ticketStats = ticketStatsQuery.data?.stats;
  const billing = billingQuery.data?.dashboard || billingQuery.data?.data;

  const activeDeals = deals.filter((deal) => ["active", "proposal", "negotiation", "prospect"].includes(deal.stage || ""));
  const activeTasks = tasks.filter((task) => !["completed", "dismissed", "done"].includes(task.status || ""));
  const pendingBugs = ticketStats?.byStatus?.pending || 0;
  const inProgressBugs = ticketStats?.byStatus?.in_progress || 0;
  const totalPipeline = useMemo(
    () => activeDeals.reduce((sum, deal) => sum + Number(deal.value || 0), 0),
    [activeDeals],
  );
  const protocolReadiness = Math.min(100, Math.round(((clients.length ? 1 : 0) + (deals.length ? 1 : 0) + (ticketStats ? 1 : 0) + (billing ? 1 : 0)) * 25));

  return (
    <div className="space-y-6">
      <div className="flex flex-col justify-between gap-4 border-b border-slate-200 pb-5 lg:flex-row lg:items-end">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.08em] text-orange-600">Owner desktop</div>
          <h1 className="mt-2 text-3xl font-black tracking-tight text-slate-950">BSA Desktop Control</h1>
          <p className="mt-2 max-w-3xl text-sm font-medium text-slate-600">
            Full desktop system for clients, deals, bugs, billing, onboarding, and launch control.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button onClick={() => navigate("/admin/clients")} className="gap-2 bg-slate-950 hover:bg-slate-800">
            <UserPlus className="h-4 w-4" />
            Clients
          </Button>
          <Button onClick={() => navigate("/admin/tickets")} variant="outline" className="gap-2">
            <Bug className="h-4 w-4" />
            Bugs
          </Button>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <Metric label="Active pipeline" value={currency(totalPipeline)} icon={Handshake} tone="emerald" />
        <Metric label="Client records" value={String(clients.length)} icon={Users} tone="blue" />
        <Metric label="Open bugs" value={String(pendingBugs + inProgressBugs)} icon={Bug} tone="orange" />
        <Metric label="Active tasks" value={String(activeTasks.length)} icon={ListTodo} />
      </div>

      <div className="grid gap-6 xl:grid-cols-[1.45fr_0.9fr]">
        <Panel
          title="Deal Command"
          eyebrow="Revenue and delivery"
          action={
            <Button variant="ghost" size="sm" onClick={() => navigate("/admin/deals")} className="gap-2">
              Open deals <ArrowUpRight className="h-4 w-4" />
            </Button>
          }
        >
          <div className="overflow-hidden rounded-md border border-slate-200">
            <table className="w-full text-left text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-[0.06em] text-slate-500">
                <tr>
                  <th className="px-4 py-3 font-bold">Deal</th>
                  <th className="px-4 py-3 font-bold">Client</th>
                  <th className="px-4 py-3 font-bold">Stage</th>
                  <th className="px-4 py-3 text-right font-bold">Value</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {activeDeals.slice(0, 8).map((deal) => (
                  <tr
                    key={deal.id}
                    onClick={() => navigate(`/admin/deals/${deal.id}`)}
                    className="cursor-pointer bg-white hover:bg-slate-50"
                  >
                    <td className="px-4 py-3">
                      <div className="font-bold text-slate-950">{deal.name}</div>
                      <div className="text-xs text-slate-500">Updated {dateLabel(deal.updatedAt || deal.createdAt)}</div>
                    </td>
                    <td className="px-4 py-3 text-slate-600">{deal.clientName || "No client"}</td>
                    <td className="px-4 py-3">
                      <Badge variant="outline" className={cn("capitalize", statusClass(deal.stage))}>
                        {deal.stage || "unknown"}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-right font-black text-slate-950">{currency(deal.value)}</td>
                  </tr>
                ))}
                {activeDeals.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="px-4 py-8 text-center text-sm font-semibold text-slate-500">
                      No active deals loaded.
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </Panel>

        <Panel title="Owner Actions" eyebrow="Daily control">
          <div className="space-y-3">
            {ownerActions.map((action) => {
              const Icon = action.icon;
              return (
                <button
                  key={action.href}
                  onClick={() => navigate(action.href)}
                  className="flex w-full items-center gap-3 rounded-md border border-slate-200 p-3 text-left transition-colors hover:border-slate-300 hover:bg-slate-50"
                >
                  <span className="flex h-10 w-10 items-center justify-center rounded-md bg-slate-950 text-white">
                    <Icon className="h-4 w-4" />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block font-black text-slate-950">{action.label}</span>
                    <span className="block truncate text-xs font-medium text-slate-500">{action.detail}</span>
                  </span>
                  <ArrowUpRight className="h-4 w-4 text-slate-400" />
                </button>
              );
            })}
          </div>
        </Panel>
      </div>

      <div className="grid gap-6 xl:grid-cols-3">
        <Panel title="BSA Protocol Status" eyebrow="Reusable systems">
          <div className="mb-4">
            <div className="mb-2 flex justify-between text-sm font-bold">
              <span>System readiness</span>
              <span>{protocolReadiness}%</span>
            </div>
            <Progress value={protocolReadiness} className="h-2" />
          </div>
          <div className="space-y-2">
            {bsaProtocols.map((protocol) => (
              <div key={protocol} className="flex items-start gap-2 text-sm font-medium text-slate-700">
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
                {protocol}
              </div>
            ))}
          </div>
        </Panel>

        <Panel
          title="Bug Pipeline"
          eyebrow="Developer tracker"
          action={
            <Button variant="ghost" size="sm" onClick={() => navigate("/admin/tickets")} className="gap-2">
              Open <ArrowUpRight className="h-4 w-4" />
            </Button>
          }
        >
          <div className="space-y-3">
            {[
              ["Pending", pendingBugs, "bg-amber-50 text-amber-700"],
              ["In progress", inProgressBugs, "bg-blue-50 text-blue-700"],
              ["Resolved", ticketStats?.byStatus?.resolved || 0, "bg-emerald-50 text-emerald-700"],
              ["Ready to bill", currency(ticketStats?.totalUnbilledAmount || 0), "bg-slate-100 text-slate-700"],
            ].map(([label, value, className]) => (
              <div key={label} className="flex items-center justify-between rounded-md border border-slate-200 px-3 py-2">
                <span className="text-sm font-bold text-slate-600">{label}</span>
                <span className={cn("rounded px-2 py-1 text-sm font-black", className as string)}>{value}</span>
              </div>
            ))}
          </div>
        </Panel>

        <Panel title="Money Control" eyebrow="BillingOps">
          <div className="space-y-3">
            <div className="flex items-center justify-between rounded-md border border-slate-200 px-3 py-2">
              <span className="flex items-center gap-2 text-sm font-bold text-slate-600">
                <CreditCard className="h-4 w-4" />
                Revenue
              </span>
              <span className="font-black">{currency(billing?.totalRevenue || billing?.monthlyRevenue || 0)}</span>
            </div>
            <div className="flex items-center justify-between rounded-md border border-slate-200 px-3 py-2">
              <span className="flex items-center gap-2 text-sm font-bold text-slate-600">
                <AlertTriangle className="h-4 w-4" />
                Outstanding
              </span>
              <span className="font-black">{currency(billing?.outstandingBalance || billing?.unpaidAmount || 0)}</span>
            </div>
            <Button variant="outline" className="w-full gap-2" onClick={() => navigate("/admin/billing")}>
              Billing center <ArrowUpRight className="h-4 w-4" />
            </Button>
          </div>
        </Panel>
      </div>

      <div className="grid gap-6 xl:grid-cols-2">
        <Panel title="Recent Clients" eyebrow="Contact records">
          <div className="grid gap-3 md:grid-cols-2">
            {clients.slice(0, 8).map((client) => (
              <button
                key={client.id}
                onClick={() => navigate(`/admin/clients/${client.id}`)}
                className="rounded-md border border-slate-200 p-3 text-left transition-colors hover:border-slate-300 hover:bg-slate-50"
              >
                <div className="font-black text-slate-950">{client.name}</div>
                <div className="mt-1 flex items-center gap-2 text-xs font-medium text-slate-500">
                  <Mail className="h-3.5 w-3.5" />
                  <span className="truncate">{client.email || "No email"}</span>
                </div>
              </button>
            ))}
          </div>
        </Panel>

        <Panel
          title="Active Tasks"
          eyebrow="Execution"
          action={
            <Button variant="ghost" size="sm" onClick={() => navigate("/admin/tasks")} className="gap-2">
              Open tasks <ArrowUpRight className="h-4 w-4" />
            </Button>
          }
        >
          <div className="space-y-2">
            {activeTasks.slice(0, 8).map((task) => (
              <div key={task.id} className="flex items-start gap-3 rounded-md border border-slate-200 px-3 py-2">
                <MessageSquare className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
                <div className="min-w-0">
                  <div className="truncate text-sm font-bold text-slate-950">{task.title}</div>
                  <div className="text-xs font-medium text-slate-500">
                    {[task.company, task.project, task.priority].filter(Boolean).join(" · ") || "BSA"}
                  </div>
                </div>
              </div>
            ))}
            {activeTasks.length === 0 ? (
              <div className="rounded-md border border-slate-200 px-3 py-6 text-center text-sm font-semibold text-slate-500">
                No active tasks loaded.
              </div>
            ) : null}
          </div>
        </Panel>
      </div>
    </div>
  );
}
